import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, renderSlot as _renderSlot, createVNode as _createVNode, mergeProps as _mergeProps, withCtx as _withCtx, renderList as _renderList, createSlots as _createSlots, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import VueDatepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import BaseInput from '@/components/BaseInput.vue'
import { computed, PropType, ref, watch } from 'vue'
import {
  formatIntlDateAndTime,
  formatIntlDateOnly,
  formatWithLocale,
  getGlobalLocale,
} from '@/utils/formatters'
import { StringDate, StringDateTime, StringTime } from '@/models/shared.model'
import { YearMonth } from '@/models/calendar.model'
import { addYears } from 'date-fns'

// value of @update-year-month event from vue-datePicker component
export interface UpdateYearMonthEvent {
  instance: number
  month: number
  year: number
}


export default /*#__PURE__*/_defineComponent({
  __name: 'DatePicker',
  props: {
  modelValue: {
    type: [Object, Array, String, null] as PropType<
      | YearMonth
      | StringDateTime
      | StringDateTime[]
      | StringDate
      | StringDate[]
      | StringTime
      | StringTime[]
      | {
          // time picker
          hours: number | string
          minutes: number | string
          seconds?: number | string
        }
      | null
    >,
    required: true,
  },
  label: {
    type: String as PropType<string>,
    default: '',
  },
  required: {
    type: Boolean,
    default: false,
  },
  name: {
    type: String as PropType<string | null>,
    default: null,
  },
  placeholder: {
    type: String,
    default: undefined,
  },
  errors: {
    type: Array as PropType<string[] | undefined>,
    default: undefined,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  autoApply: {
    type: Boolean as PropType<boolean>,
    default: true,
  },
  clearable: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  monthPicker: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  timePicker: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  range: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  multiDates: {
    // Could also be MultiDatesConfig
    type: Boolean,
    default: false,
  },
  enableTimePicker: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  minutesIncrement: {
    type: String as PropType<string>,
    default: '30',
  },
  minutesGridIncrement: {
    type: String as PropType<string>,
    default: '30',
  },
  flow: {
    type: Array as PropType<
      (
        | 'month'
        | 'year'
        | 'calendar'
        | 'time'
        | 'minutes'
        | 'hours'
        | 'seconds'
      )[]
    >,
    default: () => [],
  },
  format: {
    type: Function,
    required: false,
  },
  monthChangeOnScroll: {
    type: Boolean,
    default: false,
  },
},
  emits: ['update:modelValue'],
  setup(__props) {

const props = __props



const maxDate = computed(() => addYears(new Date(), 1))

const localizedModelValueFormat = (dates) => {
  // Check for missing model value
  if (!props.modelValue) return null

  // If model value is array type then this means this Datepicker has range or multiDates prop
  // Else model value has single value
  if (Array.isArray(props.modelValue)) {
    if (props.multiDates) {
      return props.modelValue
        .map((selectedDate) => {
          if (props.timePicker) {
            return formatWithLocale(selectedDate, 'HH:mm')
          } else if (props.enableTimePicker) {
            return formatIntlDateAndTime(selectedDate)
          }
          return formatIntlDateOnly(selectedDate)
        })
        .join(', ')
    } else {
      const start: string = props.modelValue[0]
      const end: string = props.modelValue[1]
      if (props.timePicker) {
        return `${formatWithLocale(start, 'HH:mm')} - ${formatWithLocale(
          end,
          'HH:mm',
        )}`
      } else if (props.enableTimePicker) {
        return `${formatIntlDateAndTime(start)} - ${formatIntlDateAndTime(end)}`
      }
      return `${formatIntlDateOnly(start)} - ${formatIntlDateOnly(end)}`
    }
  } else {
    if (props.monthPicker) {
      return formatWithLocale(
        new Date(props.modelValue.year, props.modelValue.month),
        'MMM yyyy',
      )
    } else if (props.timePicker) {
      return `${props.modelValue.hours}:${props.modelValue.minutes}`
    } else if (props.enableTimePicker) {
      return formatIntlDateAndTime(props.modelValue)
    }
    return formatIntlDateOnly(props.modelValue)
  }
}

// To force rerender component. Using key method
// https://michaelnthiessen.com/force-re-render/
const componentKey = ref(0)

// DatePciker doesn't rerender component when page language is changed
watch(
  () => getGlobalLocale(),
  () => {
    componentKey.value += 1
  },
)



return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(VueDatepicker), _mergeProps({
    key: componentKey.value,
    "model-value": __props.modelValue,
    "minutes-grid-increment": __props.minutesGridIncrement,
    "minutes-increment": __props.minutesIncrement,
    "hide-offset-dates": "",
    "start-time": { hours: 10, minutes: 0, seconds: 0 },
    locale: _ctx.$i18n.locale,
    "cancel-text": _ctx.$t('cancel'),
    "select-text": _ctx.$t('confirm'),
    name: __props.name,
    "auto-apply": __props.autoApply,
    clearable: __props.clearable,
    "enable-time-picker": __props.enableTimePicker,
    disabled: __props.disabled
  }, _ctx.$attrs, {
    format: __props.format ?? localizedModelValueFormat,
    "month-picker": __props.monthPicker,
    "time-picker": __props.timePicker,
    range: __props.range,
    "multi-dates": __props.multiDates,
    flow: __props.flow,
    teleport: true,
    "month-change-on-scroll": __props.monthChangeOnScroll,
    "max-date": _unref(maxDate),
    "onUpdate:modelValue": _cache[0] || (_cache[0] = (value) => _ctx.$emit('update:modelValue', value))
  }), _createSlots({
    "dp-input": _withCtx(({ value }) => [
      _renderSlot(_ctx.$slots, "dp-input", { value: value }, () => [
        _createVNode(BaseInput, {
          required: __props.required,
          label: __props.label,
          "model-value": value,
          name: __props.name ? `${__props.name}-trigger` : 'trigger',
          placeholder: __props.placeholder,
          readonly: "",
          disabled: __props.disabled,
          type: "text",
          "icon-left": "calendar-blank",
          class: "cursor-pointer",
          errors: __props.errors
        }, null, 8, ["required", "label", "model-value", "name", "placeholder", "disabled", "errors"])
      ])
    ]),
    _: 2
  }, [
    _renderList(_ctx.$slots, (_, slot) => {
      return {
        name: slot,
        fn: _withCtx((scope) => [
          _renderSlot(_ctx.$slots, slot, _normalizeProps(_guardReactiveProps(scope)))
        ])
      }
    })
  ]), 1040, ["model-value", "minutes-grid-increment", "minutes-increment", "locale", "cancel-text", "select-text", "name", "auto-apply", "clearable", "enable-time-picker", "disabled", "format", "month-picker", "time-picker", "range", "multi-dates", "flow", "month-change-on-scroll", "max-date"]))
}
}

})